import React, { useState, useContext } from "react";
import { FORM_FIELDS, PLACEHOLDERS, LABELS } from "../../constants";
import Input from "~/components/Input/Input";
import PhoneNumberInput from "~/components/PhoneNumberInput/PhoneNumberInput";
import Button from "~/components/Button/Button";
import SiteContext from "~/contexts/SiteContext";
import { Form } from "@remix-run/react";

import styles from "./PersonalDetailsForm.module.css";
import AuthContext from "~/contexts/AuthContext";
import { isNonEmptyString } from "~/helpers/validationHelpers";
import { BaseUser } from "~/entities/authEntities";
import Checkbox from "~/components/Checkbox/Checkbox";
import { getDateYearsAgo } from "~/helpers/dateHelper";
import SettingsContext from "~/contexts/SettingsContext";
import ThemeContext from "~/contexts/ThemeContext";
import OrderContext from "~/contexts/OrderContext";
import { clsx } from "clsx";
import ChangePhonePopover from "~/modules/auth/containers/ChangePhonePopover/ChangePhonePopover";

export interface PersonalDetailsFormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  birthday?: string;
  termsOfUse: boolean;
  joinLoyalty: boolean;
}

type PersonalDetailsFromProps = {
  loading?: boolean;
  initialData?: BaseUser;
  onSubmit: (
    values: PersonalDetailsFormValues,
    allowUpdateCustomerDetails: boolean
  ) => void;
  onCancel?: VoidFunction;
  isSignup?: boolean;
  isEditingAccount?: boolean;
  classNameForm?: string;
  classNameFooter?: string;
  isCheckoutPage?: boolean;
};

const PersonalDetailsForm: React.FC<PersonalDetailsFromProps> = ({
  loading,
  initialData,
  onSubmit,
  onCancel,
  isSignup,
  isEditingAccount,
  classNameForm,
  classNameFooter,
  isCheckoutPage,
}) => {
  const { template } = useContext(ThemeContext);
  const { site } = useContext(SiteContext);
  const { readonlyCart } = useContext(OrderContext);

  const readonlyForm = readonlyCart && !isEditingAccount && !isSignup;

  const {
    verifiedEmail,
    verifiedPhone,
    authenticatedUser,
    useComoAuth,
    customerSignInOptions,
  } = useContext(AuthContext);
  const { orgSettings } = useContext(SettingsContext);
  const [formData, setFormData] = useState<PersonalDetailsFormValues>({
    firstName: initialData?.firstName ?? authenticatedUser?.firstName ?? "",
    lastName: initialData?.lastName ?? authenticatedUser?.lastName ?? "",
    phone: verifiedPhone
      ? verifiedPhone
      : initialData?.phone ?? authenticatedUser?.phone ?? "",
    email: verifiedEmail
      ? verifiedEmail
      : initialData?.email ?? authenticatedUser?.email ?? "",
    termsOfUse: false,
    joinLoyalty: false,
    birthday: authenticatedUser?.birthday ?? "",
  });
  const [allowSavePersonalData, setAllowSavePersonalData] = useState(true);

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      termsOfUse: false,
      joinLoyalty: false,
      birthday: "",
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneInputChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      onSubmit({ ...formData }, allowSavePersonalData);
    } catch (error) {
      console.error("Failed to update personal details:", error);
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancel && onCancel();
  };

  const disabledPhoneInput =
    isNonEmptyString(verifiedPhone) ||
    readonlyForm ||
    (!!authenticatedUser && !!useComoAuth);

  return (
    <Form onSubmit={handleSubmit} className={clsx(styles.form, classNameForm)}>
      <div className={styles.formGroup}>
        <label htmlFor={FORM_FIELDS.FIRST_NAME}>First name</label>
        <Input
          type="text"
          name={FORM_FIELDS.FIRST_NAME}
          placeholder={PLACEHOLDERS.FIRST_NAME}
          value={formData.firstName}
          onChange={handleInputChange}
          required
          disabled={readonlyForm}
        />
      </div>
      <div className={styles.formGroup}>
        <label htmlFor={FORM_FIELDS.LAST_NAME}>Last name</label>
        <Input
          type="text"
          name={FORM_FIELDS.LAST_NAME}
          placeholder={PLACEHOLDERS.LAST_NAME}
          value={formData.lastName}
          onChange={handleInputChange}
          required
          disabled={readonlyForm}
        />
      </div>
      <div className={styles.formGroup}>
        <label htmlFor={FORM_FIELDS.PHONE}>
          Phone number {disabledPhoneInput && <ChangePhonePopover />}
        </label>
        <PhoneNumberInput
          required
          name={FORM_FIELDS.PHONE}
          placeholder={PLACEHOLDERS.PHONE_NUMBER}
          value={formData.phone}
          onChange={handlePhoneInputChange}
          defaultCountry={site?.countryCode}
          allowedCountryCodes={customerSignInOptions?.smsAllowedCountryCodes}
          disabled={disabledPhoneInput}
          id="phone"
        />
      </div>
      <div className={styles.formGroup}>
        <label htmlFor={FORM_FIELDS.EMAIL}>Email address</label>
        <Input
          type="email"
          name={FORM_FIELDS.EMAIL}
          placeholder={PLACEHOLDERS.EMAIL_ADDRESS}
          value={formData.email}
          onChange={handleInputChange}
          disabled={isNonEmptyString(verifiedEmail) || readonlyForm}
          required
        />
      </div>
      {useComoAuth && (isSignup || (authenticatedUser && !isCheckoutPage)) && (
        <div className={styles.formGroup}>
          <label htmlFor="birthday">Birthday (optional)</label>
          <Input
            id="birthday"
            type="date"
            name={FORM_FIELDS.BIRTHDAY}
            placeholder={PLACEHOLDERS.BIRTHDAY}
            value={formData.birthday}
            onChange={handleInputChange}
            max={getDateYearsAgo(16)} // Validation as required by Como
            disabled={readonlyForm || !!authenticatedUser}
          />
        </div>
      )}
      {isCheckoutPage && isSignup != true && (
        <div>
          <div className={styles.formGroup}>
            <span>
              <Checkbox
                checked={allowSavePersonalData}
                onCheck={setAllowSavePersonalData}
                disabled={readonlyForm}
              />
              <span>
                Save these details{" "}
                {authenticatedUser ? "on my account" : " for my next order"}
              </span>
            </span>
          </div>
        </div>
      )}
      {isSignup && (
        <div className={styles.formGroup}>
          <span>
            <Checkbox
              checked={formData.termsOfUse}
              onCheck={(checked) =>
                setFormData((prev) => ({ ...prev, termsOfUse: checked }))
              }
              required
              disabled={readonlyForm}
            />
            <div>
              I have read and agree to the{" "}
              <a
                href={template.global.links.termsAndConditions}
                target="_blank"
                rel="noreferrer"
              >
                Terms & Conditions
              </a>{" "}
              and{" "}
              <a
                href={template.global.links.privacyPolicy}
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
            </div>
          </span>
          {useComoAuth && (
            <span>
              <Checkbox
                checked={formData.joinLoyalty}
                onCheck={(checked) =>
                  setFormData((prev) => ({ ...prev, joinLoyalty: checked }))
                }
                required={orgSettings?.loyaltySettings?.required}
                disabled={readonlyForm}
              />
              <div>
                Sign me up for{" "}
                {orgSettings?.loyaltySettings?.loyaltyLink ? (
                  <>
                    <a
                      href={orgSettings.loyaltySettings.loyaltyLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {orgSettings.loyaltySettings.loyaltyName || "Loyalty"}
                    </a>
                    {orgSettings.loyaltySettings.loyaltyName &&
                      " (Loyalty Program)"}
                  </>
                ) : orgSettings?.loyaltySettings?.loyaltyName ? (
                  `${orgSettings.loyaltySettings.loyaltyName} (Loyalty Program)`
                ) : (
                  "Loyalty"
                )}
              </div>
            </span>
          )}
        </div>
      )}

      <div className={clsx(styles.buttonsContainer, classNameFooter)}>
        <Button
          loading={loading}
          type="submit"
          style={{
            width: loading ? 140 : 100,
            padding: loading ? "0 24px 0 0" : "0 24px",
          }}
          disabled={readonlyForm}
        >
          {LABELS.SAVE}
        </Button>
        {onCancel && (
          <Button
            disabled={loading}
            variant="link"
            type="button"
            onClick={handleCancel}
          >
            {LABELS.CANCEL}
          </Button>
        )}
      </div>
    </Form>
  );
};

export default PersonalDetailsForm;
